<script>
    import Title from "$component/Title.svelte";
    import {lines, totalPrice, totalSavings, mwstHinweis, ohneMwst, store as basketStore} from '$lib/basketStore.svelte.js';
    import {steuerhinweis} from '$utils/steuerhinweis.js';
    import {t} from '$utils/lang.js';
    import {currencyFormatter} from '$utils/formatter.js'

    import {twMerge} from 'tailwind-merge';
    const {type = 'basket', class: cls = 'p-3.5 bg-white rounded-md border border-grey'} = $props()
    // Total comes from the server-side BasketSnapshot — single source of
    // truth, identical to what Express-Snapshot + Order-Finalize see.
    // EasyCredit interest is a payment-step add-on not in the basket
    // snapshot, so it's added on the client. Fallback path (snapshot null,
    // e.g. before first basket fetch) sums the same components manually.
    const grandTotal = $derived.by(() => {
        const ec = basketStore.easyCreditInterests > 0 ? basketStore.easyCreditInterests : 0;
        const snap = basketStore.snapshot;
        if (snap) return (snap.total?.brutto ?? 0) + ec;
        return totalPrice()
            + (basketStore.price?.leasingMarkup ?? 0)
            + basketStore.shipping
            + ec;
    })
</script>

{#if type === 'summary'}
    <!-- Order-Summary variant (Figma: "Deine Bestellung" sidebar).
         Rendered by Basket.svelte summary mode below the mini-products list. -->
    <div class={twMerge('flex flex-col gap-4', cls)} data-checkout-section="basket-sum" data-basket-type={type} data-basket-total={grandTotal}>
        <!-- Calculations -->
        <div class="flex flex-col gap-2 text-xsm">
            {#each lines() as line}
                <div class="flex items-center justify-between gap-1" data-checkout-line={line.label} data-checkout-value={line.value}>
                    <span class="font-light text-gray-103">{t(line.label, line.params)}</span>
                    {#if line.label === 'Versand' && !basketStore.shippingAvailable}
                        <span class="font-bold text-scarlet" data-checkout-shipping-unavailable>{t('nicht möglich')}</span>
                    {:else if line.label === 'Versand' && line.value === 0}
                        <span class="font-bold text-elm">{t('Kostenlos')}</span>
                    {:else}
                        <span class="font-bold text-[#0a0a0a]">{currencyFormatter.format(line.value)}</span>
                    {/if}
                </div>
            {/each}
        </div>
        <hr class="border-[#e5e7eb]">
        <!-- Total -->
        <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between gap-1 text-[#0a0a0a]">
                <span class="font-bold text-[17px] leading-6">{t('Gesamtsumme')}</span>
                <span class="font-black text-xl">{basketStore.shippingAvailable ? currencyFormatter.format(grandTotal) : '–'}</span>
            </div>
            <div class="text-right font-light text-gray-103 text-[11px]" data-checkout-vat-hint>{t(mwstHinweis())}</div>
            {#if steuerhinweis(ohneMwst())}
                <!-- reference: [utils/steuerhinweis 1] -->
                <div class="text-right font-light text-gray-103 text-[11px]" data-checkout-tax-note>{steuerhinweis(ohneMwst())}</div>
            {/if}
        </div>
        <!-- Overall savings -->
        {#if totalSavings() > 0}
            <div class="flex items-center gap-2 bg-[#e8f6f6] rounded-lg p-3" data-checkout-section="total-savings" data-savings-total={totalSavings()}>
                <svg class="shrink-0 size-4 text-elm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span class="font-bold text-elm text-xsm">{t('Du sparst insgesamt {betrag}', {betrag: currencyFormatter.format(totalSavings())})}</span>
            </div>
        {/if}
    </div>
{:else}
<div class={twMerge(cls)} data-checkout-section="basket-sum" data-basket-type={type} data-basket-total={grandTotal}>
    {#if type === 'basket'}
        <Title title={t("Gesamtsumme")} titleClass="leading-6.5" class="pb-3"/>
    {/if}
    <div class="flex flex-col gap-1.5"
         class:text-sm={type === 'basket'}
         class:text-xsm={type !== 'basket'}>
        {#each lines() as line}
            <div class="flex items-center justify-between gap-1" data-checkout-line={line.label} data-checkout-value={line.value}>
                <span class="font-light">{t(line.label, line.params)}</span>
                <span class={type !== 'basket' ? 'font-bold': ''}>
                    {#if line.label === 'Versand' && !basketStore.shippingAvailable}
                        <span class="text-scarlet" data-checkout-shipping-unavailable>{t('nicht möglich')}</span>
                    {:else}
                        {line.label === 'Versand' && line.value === 0 ? t('gratis') : currencyFormatter.format(line.value)}
                    {/if}
                </span>
            </div>
        {/each}
        <hr class="border-grey">
        <div class="flex items-center justify-between gap-1 font-bold" class:text-md={type !== 'basket'}>
            <span>{t("Gesamtsumme:")}</span>
            <span>{basketStore.shippingAvailable ? currencyFormatter.format(grandTotal) : '–'}</span>
        </div>
        <div class="text-right font-light text-xs" data-checkout-vat-hint>{t(mwstHinweis())}</div>
        {#if steuerhinweis(ohneMwst())}
            <!-- reference: [utils/steuerhinweis 1] -->
            <div class="text-right font-light text-xs" data-checkout-tax-note>{steuerhinweis(ohneMwst())}</div>
        {/if}
    </div>
</div>
{/if}
